// src/pages/BalanceSheet/index.js
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { DownloadOutlined, FileExcelOutlined, PrinterOutlined } from "@ant-design/icons";
import { App, Button, Card, Flex, Grid, Typography } from "antd";
import BalanceSheetFilters from "./components/BalanceSheetFilters";
import BalanceSheetTable from "./components/BalanceSheetTable";
import ReportDom from "../../../components/common/ReportDom";
import { useReactToPrint } from "react-to-print";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { exportBalanceSheetCsv } from "./components/exportBalanceSheetCsv";
import { exportBalanceSheetXlsx } from "./components/exportBalanceSheetXlsx";
import { useBalanceSheetData } from "./components/useBalanceSheetData";
import dayjs from "dayjs";
import { toCompanyDateOnlyforReportDateFilter } from "../../../lib/functions";

const { Title } = Typography;

const fmtDate = (d) => toCompanyDateOnlyforReportDateFilter(d)?.format("DD-MM-YYYY");

// Period line for the printed report, based on the filters the report was loaded with.
const getPrintDateLabel = (applied, fyStartMonth) => {
  if (!applied) return undefined;
  switch (applied.filterType) {
    case "Date Wise":
      return applied.toDate ? `As on ${fmtDate(applied.toDate)}` : undefined;
    case "Financial Year": {
      const fyYear = Number(applied.fyYear);
      if (!fyYear) return undefined;
      const start = dayjs(`${fyYear}-${String(fyStartMonth).padStart(2, "0")}-01`);
      const end = start.add(1, "year").subtract(1, "day");
      return `Financial Year ${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")}`;
    }
    case "Life Time":
      return `Life Time (as on ${dayjs().format("DD-MM-YYYY")})`;
    default:
      return undefined;
  }
};

// Zoho-style period line for the CSV / Excel export ("As of 30/09/2026").
const getExportDateLabel = (applied, fyStartMonth) => {
  if (!applied) return "";
  switch (applied.filterType) {
    case "Date Wise":
      return applied.toDate
        ? `As of ${toCompanyDateOnlyforReportDateFilter(applied.toDate)?.format("DD/MM/YYYY")}`
        : "";
    case "Financial Year": {
      const fyYear = Number(applied.fyYear);
      if (!fyYear) return "";
      const start = dayjs(`${fyYear}-${String(fyStartMonth).padStart(2, "0")}-01`);
      const end = start.add(1, "year").subtract(1, "day");
      return `From ${start.format("DD/MM/YYYY")} To ${end.format("DD/MM/YYYY")}`;
    }
    case "Life Time":
      return `As of ${dayjs().format("DD/MM/YYYY")}`;
    default:
      return "";
  }
};

const BalanceSheet = () => {
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { balances, appliedFilters } = useSelector((state) => state.balanceSheet);
  const fyStartMonth = useSelector((state) => state.institution?.data?.fy_start_month) || 1;
  const printDateLabel = getPrintDateLabel(appliedFilters, fyStartMonth);

  const { liabilities, assets } = useBalanceSheetData();

  const reportRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Balance Sheet",
    removeAfterPrint: true,
  });
  const exportParams = () => ({
    assets,
    liabilities,
    title: "Balance Sheet",
    basis: "Accrual",
    dateLabel: getExportDateLabel(appliedFilters, fyStartMonth),
    filename: "balance_sheet",
  });
  const handleDownloadCsv = () => exportBalanceSheetCsv(exportParams());
  const handleDownloadExcel = async () => {
    try {
      await exportBalanceSheetXlsx(exportParams());
    } catch (err) {
      message.error("Failed to export Excel file");
    }
  };

  const hasData = Object.keys(balances).length > 0;
  return (
    <div className="p-4">
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <Title level={4} style={{ margin: 0 }} >
            Balance Sheet
          </Title>
        }
      extra={
          <Flex gap={8}>
            {hasData && (
              <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                {!screens.xs && "CSV"}
              </Button>
            )}
            {hasData && (
              <Button icon={<FileExcelOutlined />} onClick={handleDownloadExcel}>
                {!screens.xs && "Excel"}
              </Button>
            )}
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          </Flex>
        }
        styles={{
          header: { borderBottom: "none", padding: "12px 16px" },
          body: { padding: 0 },
        }}
      />

      <BalanceSheetFilters messageApi={message} />

      <Card className="shadow-md rounded-xl">
        <BalanceSheetTable />
      </Card>

      {/* Hidden print layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Balance Sheet"
          mode="report-page"
          filters={null}
          dateLabel={printDateLabel}
          renderTable={() => <BalanceSheetTable printMode={true} />}
        />
      </div>
    </div>
  );
};

export default BalanceSheet;
